import React, { useState, useEffect } from "react";

import { Button } from "../../ui/button.tsx";

import { useUser } from "@clerk/clerk-react";

import { IntegrationFooter } from "./IntegrationFooter.tsx";

import {
    almostThereIcon,
    errorSheetIcon,
    existingGoogleSheetIcon,
    googleSheetStepOneIcon,
    googleSheetStepTwoIcon,
    hoorayIcon,
    newGoogleSheetIcon,
    successSheetIcon,
} from "./icons.js";

import { Input } from "../../ui/input.tsx";

import { LoaderCircle } from "lucide-react";

import { useApi } from "../../../Api/useApi.ts";

import { useMutation } from "@tanstack/react-query";

import { useParams } from "react-router-dom";
import { useFormEditorContext } from "../../../Contexts/FormEditorContext.jsx";

type IntegrationContentProps = {
    integrationType: string;
    setIntegrationDialog: (open: boolean) => void;
};

type GoogleSheetIntegrationParams = {
    formId: string;
    workspaceId: string;
    data: {
        type: string;
        spreadsheet_url?: string;
        spreadsheet_title?: string;
    };
};

type ExportPastDataParams = {
    formId: string;
    workspaceId: string;
    exportPastData: boolean;
};

export const IntegrationContent = ({
    integrationType,
    setIntegrationDialog,
}: IntegrationContentProps) => {
    const api = useApi();
    const { user } = useUser();
    const [step, setStep] = useState(0);
    const { id, workspaceId } = useParams();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [useExistingSheet, setUseExistingSheet] = useState<boolean | null>(
        null,
    );
    const [alreadyAuthorized, setAlreadyAuthorized] = useState<boolean>(false);
    const [existingSheetURL, setExistingSheetURL] = useState<string>("");
    const [newSheetName, setNewSheetName] = useState<string>("");
    const { setFormSettings, formSettings } = useFormEditorContext();

    useEffect(() => {
        const googleAccount = user?.externalAccounts.find(
            (account) => account.provider === "google",
        );

        if (googleAccount) {
            const requiredScopes = [
                "https://www.googleapis.com/auth/drive.file",
            ];

            const approvedScopes =
                googleAccount.approvedScopes?.split(" ") || [];
            const hasAllScopes = requiredScopes.every((scope) =>
                approvedScopes.includes(scope),
            );

            setAlreadyAuthorized(hasAllScopes);
        }
    }, [user]);

    // This function handles the reauthorization flow with Google
    async function requestGoogleDriveAccess() {
        setIsLoading(true);

        try {
            // Find the user's Google account
            const googleAccount = user?.externalAccounts.find(
                (account) => account.provider === "google",
            );

            const redirectUrl = window.location.href + "?googlesheet=true";

            if (!googleAccount) {
                console.error("No Google account connected");
                // If no Google account exists, create one first
                await user
                    ?.createExternalAccount({
                        strategy: "oauth_google",
                        redirectUrl: `${redirectUrl}`,
                        additionalScopes: [
                            "https://www.googleapis.com/auth/drive.file",
                        ],
                    })
                    .then((res) => {
                        if (
                            res?.verification?.externalVerificationRedirectURL
                        ) {
                            window.location.href =
                                res.verification.externalVerificationRedirectURL.href;
                        }
                    })
                    .catch((err) => {
                        console.log("Error:", err);
                    });
                return;
            }

            // Request reauthorization with the specific scopes you need
            const reauth = await googleAccount.reauthorize({
                redirectUrl: window.location.href + "?googlesheet=true",
                additionalScopes: [
                    "https://www.googleapis.com/auth/drive.file",
                ],
            });

            // Redirect user to Google consent screen
            if (reauth?.verification?.externalVerificationRedirectURL) {
                window.location.href =
                    reauth.verification.externalVerificationRedirectURL.href;
            }
        } catch (error) {
            console.error("Error requesting Google Drive access:", error);
        } finally {
            setIsLoading(false);
        }
    }

    const googleSheetIntegrationMutation = useMutation({
        mutationFn: (params: GoogleSheetIntegrationParams) =>
            api.integrations.integration(
                params.formId,
                params.workspaceId,
                params.data,
            ),
        onSuccess: (data) => {
            setFormSettings((prev) => ({
                ...prev,
                integrations: {
                    google_sheets_integration: {
                        type: data?.data?.type,
                        spreadsheet_url: data.data?.spreadsheet_url,
                        status: data.data?.status,
                    },
                },
            }));
            setStep(4);
        },

        onError: (error) => {
            setStep(5);
            console.error("Error details:", error);
        },
    });

    const {
        mutate: exportPastDataMutation,
        isPending: exportingData,
        isSuccess: exported,
        isError: exportError,
        error: exportErrorDetails,
    } = useMutation({
        mutationFn: (params: ExportPastDataParams) =>
            api.integrations.exportPastResponses(
                params.formId,
                params.workspaceId,
                params.exportPastData,
            ),
        onSuccess: () => {
            setStep(6);
        },

        onError: (error) => {
            setStep(4);
            console.error("Error details:", error);
        },
    });

    const handleActivation = () => {
        const value = useExistingSheet ? existingSheetURL : newSheetName;

        const data = {
            type: "google_sheets_integration",
            [useExistingSheet ? "spreadsheet_url" : "spreadsheet_title"]: value,
        };

        if (id && workspaceId) {
            setStep(3);
            googleSheetIntegrationMutation.mutate({
                formId: id,
                workspaceId: workspaceId,
                data: data,
            });
        }
    };

    const handleExportPastData = () => {
        if (id && workspaceId) {
            exportPastDataMutation({
                formId: id,
                workspaceId: workspaceId,
                exportPastData: true,
            });
        }
    };

    const openSpreadsheet = () => {
        const spreadsheetUrl =
            formSettings?.integrations?.google_sheets_integration
                ?.spreadsheet_url;
        if (spreadsheetUrl) {
            //close the integration dialog
            setIntegrationDialog(false);
            // Open the spreadsheet URL in a new tab
            window.open(spreadsheetUrl, "_blank");
        }
    };

    if (step === 0) {
        return (
            <div className="flex flex-col w-full h-full">
                <StepsLayout
                    icon={googleSheetStepOneIcon}
                    title="Connect to your Google account"
                    description="There’s no Google account linked with Buildform yet"
                >
                    {!alreadyAuthorized ? (
                        <Button
                            onClick={requestGoogleDriveAccess}
                            disabled={isLoading}
                            className="text-white w-fit text-xs rounded-[8px] py-2.5 px-3 h-9 bg-primary hover:bg-primary/80"
                        >
                            {isLoading ? "Connecting..." : "Login to Google"}
                        </Button>
                    ) : (
                        setStep(1)
                    )}
                </StepsLayout>
                <IntegrationFooter
                    integrationType={integrationType}
                    currentStep={step}
                />
            </div>
        );
    }

    /* GETTING ACCESS AND CHOOSE TYPE TO GO WITH WHETHER A NEW OR EXISTING SHEET */
    if (step === 1) {
        return (
            <div className="flex flex-col w-full h-full ">
                {/*    {useExistingSheet === null ? (
                    <StepsLayout
                        icon={googleSheetStepTwoIcon}
                        title="Where do you want to send your responses?"
                        description="You can create a new sheet or use an existing one."
                    >
                        <div className="flex justify-center gap-2 ">
                            <Button
                                className="w-fit bg-[#F1F2F4] hover:bg-[#F1F2F4] text-xs py-2.5 px-3 text-primary"
                                onClick={() => setUseExistingSheet(false)}
                            >
                                Create new sheet
                            </Button>
                            <Button
                                className="w-fit bg-[#F1F2F4] hover:bg-[#F1F2F4] text-xs py-2.5 px-3 text-primary"
                                onClick={() => setUseExistingSheet(true)}
                            >
                                Use existing
                            </Button>
                        </div>
                    </StepsLayout>
                ) : useExistingSheet ? (
                    <StepsLayout
                        icon={existingGoogleSheetIcon}
                        title="Paste your spreadsheet’s URL here"
                        description="Provide the URL of your spreadsheet to connect and start using it."
                    >
                        <div className="flex flex-col gap-2 ">
                            <Input
                                placeholder="e.g. https://docs.google.com/spreadsheets/"
                                value={existingSheetURL}
                                onChange={(e) =>
                                    setExistingSheetURL(e.target.value)
                                }
                                className="w-[300px] focus-visible:ring-offset-0 focus-visible:outline-none focus-visible:ring-0  h-9"
                                type="url"
                                required
                                pattern="https://docs\.google\.com/spreadsheets/d/[^/]+"
                                title="Please enter a valid Google Sheets URL"
                                aria-label="Google Sheets URL"
                                aria-describedby="google-sheet-url"
                                id="google-sheet-url"
                                autoFocus
                            />
                            <Button
                                className=" bg-primary h-9 rounded-[8px] hover:bg-primary text-xs py-2.5 px-3 text-white w-[300px]"
                                onClick={() => {
                                    console.log(
                                        "Connecting to sheet:",
                                        existingSheetURL,
                                    );
                                    setStep(2);
                                }}
                                disabled={!existingSheetURL}
                            >
                                Connect to spreadsheet
                            </Button>
                        </div>
                    </StepsLayout>
                ) : ( */}
                <StepsLayout
                    icon={newGoogleSheetIcon}
                    title="Name your new spreadsheet"
                    description="Enter a name for your new spreadsheet to get started."
                >
                    <div className="flex flex-col gap-2 ">
                        <Input
                            placeholder="e.g. Product survey results"
                            value={newSheetName}
                            onChange={(e) => setNewSheetName(e.target.value)}
                            className="w-[300px] focus-visible:ring-offset-0 focus-visible:outline-none focus-visible:ring-0  h-9"
                            required
                            autoFocus
                        />
                        <Button
                            className="bg-primary h-9 rounded-[8px] hover:bg-primary text-xs py-2.5 px-3 text-white w-[300px]"
                            onClick={() => {
                                setStep(2);
                            }}
                            disabled={newSheetName.trim() === ""}
                        >
                            Create new sheet
                        </Button>
                    </div>
                </StepsLayout>
                {/* )} */}

                <IntegrationFooter
                    integrationType={integrationType}
                    currentStep={step}
                />
            </div>
        );
    }

    /* ACTIVATE STEP */
    if (step === 2) {
        return (
            <div className="flex flex-col w-full h-full ">
                <StepsLayout
                    icon={almostThereIcon}
                    title="Almost there..."
                    description="Click Activate Integration to save and finish the setup."
                >
                    <Button
                        className=" mt-2 bg-primary h-9 rounded-[8px] hover:bg-primary text-xs py-2.5 px-3 text-white w-[135px]"
                        onClick={handleActivation}
                    >
                        Activate integration
                    </Button>
                </StepsLayout>
                <IntegrationFooter
                    integrationType={integrationType}
                    currentStep={step}
                />
            </div>
        );
    }

    /* LOADING STEP */
    if (step === 3) {
        return (
            <div className="flex flex-col w-full h-full ">
                <StepsLayout>
                    <div className="flex items-center gap-2">
                        <LoaderCircle
                            size={16}
                            className="animate-spin text-primary"
                        />
                        <span className="text-xs font-normal leading-none tracking-tight ">
                            Just a moment - just checking your
                            <b> Google integration</b>
                        </span>
                    </div>
                </StepsLayout>
            </div>
        );
    }
    /* SUCCESS STEP */
    if (step === 4) {
        return (
            <div className="flex flex-col w-full h-full ">
                <StepsLayout
                    icon={successSheetIcon}
                    title="Looks like you already collected some responses"
                    description="Do you want to send them to your spreadsheet?"
                >
                    <div className="flex items-center gap-2 ">
                        <Button
                            className="bg-primary h-9 rounded-[8px] hover:bg-primary text-xs py-2.5 px-3 text-white w-[135px]"
                            onClick={handleExportPastData}
                            disabled={
                                exportingData ||
                                exported ||
                                // @ts-ignore
                                exportErrorDetails?.status === 404
                            }
                        >
                            {exportingData
                                ? "Sending..."
                                : exported
                                ? "Sent"
                                : // @ts-ignore
                                exportErrorDetails?.status === 404
                                ? "No responses found"
                                : exportError
                                ? "Retry"
                                : "Yes, send them"}
                        </Button>
                        <Button
                            className="w-fit bg-[#F1F2F4] h-9 hover:bg-[#F1F2F4] text-xs py-2.5 px-3 text-primary"
                            onClick={() => {
                                setIntegrationDialog(false);
                            }}
                        >
                            No thanks
                        </Button>
                    </div>
                </StepsLayout>
                <IntegrationFooter
                    integrationType={integrationType}
                    currentStep={step}
                />
            </div>
        );
    }

    /* ERROR STEP */
    if (step === 5) {
        return (
            <StepsLayout
                icon={errorSheetIcon}
                title="Ooops! Something went wrong"
                description="We couldn’t save your integration. Please try saving again."
            >
                <div className="flex items-center gap-2 ">
                    <Button
                        className="bg-primary h-9 rounded-[8px] hover:bg-primary text-xs py-2.5 px-3 text-white w-[135px]"
                        onClick={handleActivation}
                    >
                        Retry
                    </Button>
                    <Button
                        className="w-fit bg-[#F1F2F4] h-9 hover:bg-[#F1F2F4] text-xs py-2.5 px-3 text-primary"
                        onClick={() => {
                            setStep(2);
                        }}
                    >
                        Start over
                    </Button>
                </div>
            </StepsLayout>
        );
    }

    /* Hooray screen */
    if (step === 6) {
        return (
            <div className="flex flex-col w-full h-full ">
                <StepsLayout
                    icon={hoorayIcon}
                    title="You’re All Set!
Your spreadsheet is now connected."
                    description="From now on, every response will automatically be sent there in real time."
                >
                    <Button
                        className="bg-primary h-9 rounded-[8px] hover:bg-primary text-xs py-2.5 px-3 text-white"
                        onClick={openSpreadsheet}
                    >
                        Open My Spreadsheet
                    </Button>
                </StepsLayout>
                <IntegrationFooter
                    integrationType={integrationType}
                    currentStep={step}
                />
            </div>
        );
    }

    return <div className="text-sm text-muted-foreground">Step not found.</div>;
};

type StepsLayoutProps = {
    icon?: React.ReactNode;
    title?: string;
    description?: string;
    children?: React.ReactNode;
};

export const StepsLayout = ({
    icon,
    title,
    description,
    children,
}: StepsLayoutProps) => {
    return (
        <div className="flex flex-col h-[478px] items-center justify-center w-full gap-4">
            {icon}

            <div className="flex flex-col items-center justify-center gap-1 ">
                <h3 className="text-base font-medium ">{title}</h3>
                <p className="text-sm text-muted-foreground">{description}</p>
            </div>
            {children}
        </div>
    );
};
